import React, { useContext, useState } from "react";
import {
  Button,
  Grid,
  Typography,
  CircularProgress,
  Box
} from "@mui/material";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useDropzone } from 'react-dropzone';
import { styled } from '@mui/material/styles';
import { AppContext } from "../context/AppProvider";
import { AuthContext } from "../context/AuthProvider";

import { generateFilename } from "../utils/index.js";
import { fetchWithInterceptor } from "../utils/fetchWithInterceptor.js";

const DropArea = styled('div')(({ theme }) => ({
  border: '2px dashed #aaa',
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(4),
  textAlign: 'center',
  cursor: 'pointer',
  backgroundColor: theme.palette.background.default,
}));

export default function UploadAglcDialog({  onUploadSuccess, confirmationOrderId }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmationLink, setConfirmationLink] = useState(null);

  const context = useContext(AppContext);
  const authContext = useContext(AuthContext);

  const handleChangeFile = (files) => {
    setFile(files[0]);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: acceptedFiles => handleChangeFile(acceptedFiles),
    maxFiles: 1,
    maxSize: 5000000,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.csv']
    }
  });

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('purchaseOrderId', confirmationOrderId);
      const response = await fetchWithInterceptor('/api/integrations/upload-confirmation-csv', {
        method: 'POST',
        body: formData,
        headers: {
          'x-access-token': authContext.getToken(),
        },
      });

      if (!response.ok) throw new Error('Failed to upload confirmation file');

      const blob = await response.blob();   

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${generateFilename("aglc_confirmation")}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);

      context.showMessage("Confirmation uploaded successfully", "success");
      if (onUploadSuccess) {
        setConfirmationLink(url);
        console.log('url:', url);
        onUploadSuccess({ confirmationUrl: url });
      }
    } catch (error) {
      console.error(error);
      context.showMessage("Error uploading confirmation", "error");
    } finally {
      setLoading(false);
      setFile(null)
    }
  };

  return (
    <Box>
      <Grid container spacing={2} sx={{ width: '100%' }}>
        <Grid columns={12}>
          <Box sx={{ minHeight: 200 }}>
            <DropArea {...getRootProps()}>
              <input {...getInputProps()} />
              <CloudUploadIcon sx={{ fontSize: 40, mb: 1 }} />
              <Typography sx={{ textAlign: 'center', fontSize: '1.5rem' }}>
                Drag and drop a confirmation CSV file here or click
              </Typography>
            </DropArea>
             <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 4 }}>
              <Typography variant="subtitle1" sx={{ mb: 1, textAlign: 'center' }}>
                Uploaded file:{" "}
                {confirmationLink ? (
                  <Box component="a" href={confirmationLink} target="_blank" sx={{ textDecoration: 'underline', color: 'primary.main' }}>
                    {file?.name || 'Download uploaded file'}
                  </Box>
                ) : (
                  file?.name || '-'
                )}
              </Typography>
              <Button color="primary" variant="contained" onClick={handleSubmit} disabled={loading || !file}>
                {loading ? <CircularProgress size={24} color="inherit" /> : "Submit"}
              </Button>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
